import React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Horizontal stage stepper on Deal detail.
 * Current stage uses shared Button size="sm" (h-9); pill shape is intentional for the stepper.
 */
export default function StageProgress({
  stages,
  current,
  onSelect,
}: {
  stages: { id: string; name: string }[];
  current: string;
  onSelect?: (id: string) => void;
}) {
  const idx = Math.max(
    0,
    stages.findIndex((s) => s.id === current),
  );
  return (
    <div className="flex w-full items-center">
      {stages.map((s, i) => {
        const active = i <= idx;
        const isCurrent = i === idx;
        return (
          <React.Fragment key={s.id}>
            {isCurrent ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onSelect?.(s.id)}
                className={cn(
                  "h-9 flex-shrink-0 gap-2 rounded-full border px-3 shadow-none",
                  active
                    ? "border-primary bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary"
                    : "border-muted-foreground/30 bg-muted/40 hover:bg-muted/60",
                )}
                aria-label={s.name}
                title={s.name}
              >
                <span
                  className={cn(
                    "h-3 w-3 flex-shrink-0 rounded-full border",
                    active
                      ? "border-primary bg-primary"
                      : "border-muted-foreground/30 bg-muted-foreground/30",
                  )}
                  aria-hidden
                />
                <span className="max-w-[12ch] truncate text-xs font-medium sm:max-w-none">
                  {s.name}
                </span>
              </Button>
            ) : (
              <button
                type="button"
                onClick={() => onSelect?.(s.id)}
                className={cn(
                  "h-3 w-3 flex-shrink-0 rounded-full border transition hover:ring-2 hover:ring-primary/30",
                  active
                    ? "border-primary bg-primary"
                    : "border-muted-foreground/30 bg-muted-foreground/30",
                )}
                aria-label={s.name}
                title={s.name}
              />
            )}
            {i < stages.length - 1 && (
              <div
                className={cn(
                  "h-[2px] flex-1",
                  i < idx ? "bg-primary" : "bg-muted-foreground/20",
                )}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}
